import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  Clock,
  ArrowLeft,
  Loader2,
  AlertCircle,
  BarChart3,
  Star,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import logger from '../utils/logger';

// MEVIA brand colors - matching platform style
const MEVIA_COLORS = {
  primary: '#1A878D',
  secondary: '#0B6A7D',
  accent: '#2BA3A8',
  light: '#E8F4F4'
};

// Enneagram Type Colors (subtle, professional palette)
const TYPE_COLORS = {
  1: '#6366F1', // Indigo
  2: '#EC4899', // Pink
  3: '#F59E0B', // Amber
  4: '#8B5CF6', // Violet
  5: '#06B6D4', // Cyan
  6: '#78716C', // Stone
  7: '#22C55E', // Green
  8: '#EF4444', // Red
  9: '#64748B'  // Slate
};

const TYPE_ICONS = {
  1: '🎯', 2: '❤️', 3: '🏆', 4: '🎨', 5: '🔬',
  6: '🛡️', 7: '🎉', 8: '⚡', 9: '☮️'
};

const EnneagramAssessment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  
  // Assessment state
  const [assessment, setAssessment] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [results, setResults] = useState(null);
  
  // UI state
  const [showInstructions, setShowInstructions] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [cycleLimitError, setCycleLimitError] = useState(null);
  
  // Texts based on language
  const texts = {
    es: {
      title: 'Evaluación de Eneagrama',
      subtitle: 'Descubre tu tipo de personalidad',
      instructions_title: 'Instrucciones',
      instructions: [
        'Se te presentarán 72 pares de afirmaciones.',
        'Para cada par, selecciona la afirmación que te describe mejor.',
        'No hay respuestas correctas o incorrectas.',
        'Responde de manera honesta y espontánea.',
        'Piensa en cómo te comportas habitualmente, no en situaciones excepcionales.',
        'La evaluación tomará aproximadamente 15-20 minutos.'
      ],
      start_button: 'Comenzar Evaluación',
      continue_button: 'Continuar Evaluación',
      question: 'Pregunta',
      of: 'de',
      select_statement: 'Selecciona la afirmación que te describe mejor:',
      previous: 'Anterior',
      next: 'Siguiente',
      complete: 'Completar',
      saving: 'Guardando...',
      loading: 'Cargando...',
      results_title: '¡Evaluación Completada!',
      your_type: 'Tu tipo de Eneagrama',
      wing: 'Ala',
      view_details: 'Ver Reporte Completo',
      back_to_dashboard: 'Volver al Dashboard',
      strengths: 'Fortalezas',
      challenges: 'Áreas de Desarrollo',
      progress: 'Progreso',
      type_distribution: 'Distribución de Tipos'
    },
    en: {
      title: 'Enneagram Assessment',
      subtitle: 'Discover your personality type',
      instructions_title: 'Instructions',
      instructions: [
        'You will be presented with 72 pairs of statements.',
        'For each pair, select the statement that best describes you.',
        'There are no right or wrong answers.',
        'Answer honestly and spontaneously.',
        'Think about how you usually behave, not in exceptional situations.',
        'The assessment will take approximately 15-20 minutes.'
      ],
      start_button: 'Start Assessment',
      continue_button: 'Continue Assessment',
      question: 'Question',
      of: 'of',
      select_statement: 'Select the statement that best describes you:',
      previous: 'Previous',
      next: 'Next',
      complete: 'Complete',
      saving: 'Saving...',
      loading: 'Loading...',
      results_title: 'Assessment Completed!',
      your_type: 'Your Enneagram Type',
      wing: 'Wing',
      view_details: 'View Full Report',
      back_to_dashboard: 'Back to Dashboard',
      strengths: 'Strengths',
      challenges: 'Development Areas',
      progress: 'Progress',
      type_distribution: 'Type Distribution'
    }
  };
  
  const txt = texts[language] || texts.es;
  
  // Load or create assessment
  useEffect(() => {
    loadAssessment();
  }, []);
  
  const loadAssessment = async () => {
    try {
      setLoading(true);
      
      // Check for existing assessments
      const assessmentsResponse = await axios.get('/enneagram/assessments');
      const assessments = assessmentsResponse.data.data.assessments;
      
      // Find in-progress or completed assessment
      const inProgress = assessments.find(a => a.status === 'en_progreso');
      const completedAssessment = assessments.find(a => a.status === 'completado');
      
      if (completedAssessment) {
        // Load completed results
        setAssessment(completedAssessment);
        await loadResults(completedAssessment.id);
        setCompleted(true);
        setShowInstructions(false);
      } else if (inProgress) {
        // Resume in-progress assessment
        setAssessment(inProgress);
        await loadPairs(inProgress.id);
        setShowInstructions(false);
      } else {
        // Show instructions for new assessment
        setShowInstructions(true);
      }
    } catch (error) {
      logger.error('Error loading assessment:', error);
      toast.error(language === 'es' ? 'Error al cargar evaluación' : 'Error loading assessment');
    } finally {
      setLoading(false);
    }
  };
  
  const createAssessment = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/enneagram/assessments', { language });
      
      if (response.data.success) {
        const newAssessment = response.data.data;
        setAssessment(newAssessment);
        await loadPairs(newAssessment.id);
        setShowInstructions(false);
        setStartTime(Date.now());
        toast.success(language === 'es' ? 'Evaluacion iniciada' : 'Assessment started');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      logger.error('Error creating assessment:', error);
      const detail = error.response?.data?.detail || '';
      // Show cycle limit error as a persistent message
      if (detail.includes('ciclo') || detail.includes('cycle') || detail.includes('completaste')) {
        setCycleLimitError(detail);
        setShowInstructions(false);
      }
      toast.error(detail || 'Error creating assessment');
    } finally {
      setLoading(false);
    }
  };
  
  const loadPairs = async (assessmentId) => {
    try {
      const response = await axios.get(`/enneagram/assessments/${assessmentId}/all-pairs`);
      if (response.data.success) {
        setPairs(response.data.data.pairs);
        // Load any previously saved responses
        const savedMap = response.data.data.saved_responses;
        if (savedMap && Object.keys(savedMap).length > 0) {
          setResponses(savedMap);
          // Find first unanswered question
          const firstUnanswered = response.data.data.pairs.findIndex(
            p => !savedMap[p.pair_number] && !savedMap[String(p.pair_number)]
          );
          if (firstUnanswered !== -1) {
            setCurrentPairIndex(firstUnanswered);
          }
        }
      }
    } catch (error) {
      logger.error('Error loading pairs:', error);
    }
  };
  
  const loadResults = async (assessmentId) => {
    try {
      const response = await axios.get(`/enneagram/assessments/${assessmentId}/results?language=${language}`);
      if (response.data.success) {
        setResults(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading results:', error);
    }
  };
  
  // Handle response selection
  const handleSelectResponse = useCallback(async (selectedCode) => {
    if (!assessment || !pairs[currentPairIndex]) return;
    
    const pair = pairs[currentPairIndex];
    const newResponses = { ...responses, [pair.pair_number]: selectedCode };
    setResponses(newResponses);
    
    // Auto-save response
    try {
      setAutoSaving(true);
      await axios.post(`/enneagram/assessments/${assessment.id}/responses`, {
        pair_number: pair.pair_number,
        statement_a_code: pair.statement_a.code,
        statement_b_code: pair.statement_b.code,
        selected_code: selectedCode,
        response_time_seconds: startTime ? (Date.now() - startTime) / 1000 : null
      });
    } catch (error) {
      logger.error('Error saving response:', error);
    } finally {
      setAutoSaving(false);
    }
    
    // Auto-advance after short delay
    if (currentPairIndex < pairs.length - 1) {
      setTimeout(() => {
        setCurrentPairIndex(prev => prev + 1);
        setStartTime(Date.now());
      }, 300);
    }
  }, [assessment, pairs, currentPairIndex, responses, startTime]);
  
  // Complete assessment
  const handleComplete = async () => {
    // Check if all questions answered
    const answeredCount = Object.keys(responses).length;
    if (answeredCount < 72) {
      toast.error(
        language === 'es' 
          ? `Faltan ${72 - answeredCount} respuestas por completar` 
          : `${72 - answeredCount} responses remaining`
      );
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`/enneagram/assessments/${assessment.id}/complete`);
      
      if (response.data.success) {
        await loadResults(assessment.id);
        setCompleted(true);
        toast.success(language === 'es' ? '¡Evaluación completada!' : 'Assessment completed!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      logger.error('Error completing assessment:', error);
      toast.error(error.response?.data?.detail || 'Error completing assessment');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Navigation
  const goToPrevious = () => {
    if (currentPairIndex > 0) {
      setCurrentPairIndex(prev => prev - 1);
      setStartTime(Date.now());
    }
  };
  
  const goToNext = () => {
    if (currentPairIndex < pairs.length - 1) {
      setCurrentPairIndex(prev => prev + 1);
      setStartTime(Date.now());
    }
  };
  
  // Calculate progress
  const answeredCount = Object.keys(responses).length;
  const progressPercentage = pairs.length > 0 ? (answeredCount / pairs.length) * 100 : 0;
  
  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: MEVIA_COLORS.primary }} />
          <p className="text-gray-600">{txt.loading}</p>
        </div>
      </div>
    );
  }
  
  // Render cycle limit error
  if (cycleLimitError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {txt.back_to_dashboard}
            </Button>
          </div>
        </header>
        <main className="max-w-2xl mx-auto py-8 px-4">
          <Card className="shadow-lg border-amber-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'es' ? 'Evaluacion no disponible' : 'Assessment not available'}
              </h3>
              <p className="text-gray-600 mb-6" data-testid="cycle-limit-message">{cycleLimitError}</p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="text-white"
                style={{ backgroundColor: MEVIA_COLORS.primary }}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {txt.back_to_dashboard}
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
  
  // Render instructions
  if (showInstructions) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {txt.back_to_dashboard}
            </Button>
            <img 
              src="/images/mevia-logo-horizontal.png" 
              alt="MEVIA" 
              className="h-8"
            />
          </div>
        </header>
        
        <main className="max-w-2xl mx-auto py-8 px-4">
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                  style={{ backgroundColor: MEVIA_COLORS.light }}
                >
                  🎭
                </div>
                <div>
                  <CardTitle className="text-xl" style={{ color: MEVIA_COLORS.secondary }}>
                    {txt.title}
                  </CardTitle>
                  <CardDescription>{txt.subtitle}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: MEVIA_COLORS.primary }}>
                <AlertCircle className="w-5 h-5" />
                {txt.instructions_title}
              </h3>
              <ul className="space-y-3 mb-8">
                {txt.instructions.map((instruction, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 text-white"
                      style={{ backgroundColor: MEVIA_COLORS.primary }}
                    >
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{instruction}</span>
                  </li>
                ))}
              </ul>
              
              <div className="flex gap-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {txt.back_to_dashboard}
                </Button>
                <Button 
                  onClick={createAssessment}
                  className="flex-1 text-white"
                  style={{ backgroundColor: MEVIA_COLORS.primary }}
                >
                  {assessment ? txt.continue_button : txt.start_button}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
  
  // Render results
  if (completed && results) {
    const primaryType = results.primary_type;
    const wingType = results.wing_type;
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {txt.back_to_dashboard}
            </Button>
            <img 
              src="/images/mevia-logo-horizontal.png" 
              alt="MEVIA" 
              className="h-8"
            />
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto py-8 px-4">
          <Card className="shadow-lg mb-6">
            <CardHeader className="border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                  style={{ backgroundColor: MEVIA_COLORS.light }}
                >
                  🎉
                </div>
                <div>
                  <CardTitle className="text-xl" style={{ color: MEVIA_COLORS.secondary }}>
                    {txt.results_title}
                  </CardTitle>
                  <CardDescription>{txt.title}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Primary Type */}
              <div className="text-center mb-8 p-6 rounded-lg" style={{ backgroundColor: MEVIA_COLORS.light }}>
                <p className="text-gray-500 mb-2">{txt.your_type}</p>
                <div className="flex items-center justify-center gap-3 mb-2">
                  <span className="text-4xl">{TYPE_ICONS[primaryType.number]}</span>
                  <div>
                    <h2 className="text-2xl font-bold" style={{ color: MEVIA_COLORS.secondary }}>
                      Tipo {primaryType.number}: {primaryType.details?.name}
                    </h2>
                    {wingType && (
                      <p className="text-gray-600">
                        {txt.wing}: {TYPE_ICONS[wingType.number]} Tipo {wingType.number} ({wingType.details?.name})
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Type Description */}
              {primaryType.details && (
                <div className="mb-8 p-4 bg-white border rounded-lg">
                  <p className="text-gray-700 leading-relaxed">
                    {primaryType.details.short_description}
                  </p>
                </div>
              )}
              
              {/* Scores Chart */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: MEVIA_COLORS.primary }}>
                  <BarChart3 className="w-5 h-5" />
                  {txt.type_distribution}
                </h3>
                <div className="space-y-2">
                  {Object.entries(results.all_percentages).map(([typeNum, percentage]) => (
                    <div key={typeNum} className="flex items-center gap-3">
                      <span className="w-8 text-center">{TYPE_ICONS[typeNum]}</span>
                      <span className="w-16 text-sm text-gray-600">Tipo {typeNum}</span>
                      <div className="flex-1 h-5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all duration-500 rounded-full"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: parseInt(typeNum) === primaryType.number 
                              ? MEVIA_COLORS.primary 
                              : TYPE_COLORS[typeNum],
                            opacity: parseInt(typeNum) === primaryType.number ? 1 : 0.6
                          }}
                        />
                      </div>
                      <span className="w-14 text-sm text-right font-medium">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Strengths and Challenges */}
              {primaryType.details && (
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <Card className="border-t-4" style={{ borderTopColor: '#22C55E' }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-green-700">
                        <Star className="w-4 h-4" />
                        {txt.strengths}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {primaryType.details.strengths?.slice(0, 4).map((strength, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="border-t-4" style={{ borderTopColor: '#F59E0B' }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                        <Target className="w-4 h-4" />
                        {txt.challenges}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {primaryType.details.challenges?.slice(0, 4).map((challenge, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {txt.back_to_dashboard}
                </Button>
                <Button 
                  onClick={() => navigate('/reports')}
                  className="flex-1 text-white"
                  style={{ backgroundColor: MEVIA_COLORS.primary }}
                >
                  {txt.view_details}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }
  
  // Render assessment questions
  const currentPair = pairs[currentPairIndex];
  const currentResponse = responses[currentPair?.pair_number];
  
  if (!currentPair) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: MEVIA_COLORS.primary }} />
          <p className="text-gray-600">{txt.loading}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/images/mevia-logo-horizontal.png" 
                alt="MEVIA" 
                className="h-7"
              />
              <Badge variant="outline" className="text-sm">
                {txt.question} {currentPairIndex + 1} {txt.of} {pairs.length}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              {autoSaving && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {txt.saving}
                </span>
              )}
              <Badge style={{ backgroundColor: MEVIA_COLORS.light, color: MEVIA_COLORS.primary }}>
                {answeredCount}/{pairs.length}
              </Badge>
            </div>
          </div>
          <Progress 
            value={progressPercentage} 
            className="h-1.5 mt-3"
          />
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto py-6 px-4">
        {/* Question Card */}
        <Card className="shadow-lg mb-6">
          <CardHeader className="pb-2 border-b">
            <CardDescription className="text-center text-base">
              {txt.select_statement}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Statement A */}
            <button
              onClick={() => handleSelectResponse(currentPair.statement_a.code)}
              className={`w-full p-5 rounded-lg border-2 text-left transition-all duration-200 ${
                currentResponse === currentPair.statement_a.code
                  ? 'shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={currentResponse === currentPair.statement_a.code ? {
                borderColor: MEVIA_COLORS.primary,
                backgroundColor: MEVIA_COLORS.light
              } : {}}
            >
              <div className="flex items-start gap-4">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-medium ${
                    currentResponse === currentPair.statement_a.code
                      ? 'text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  style={currentResponse === currentPair.statement_a.code ? {
                    backgroundColor: MEVIA_COLORS.primary
                  } : {}}
                >
                  A
                </div>
                <p className="text-gray-800 leading-relaxed">
                  {currentPair.statement_a.text}
                </p>
              </div>
            </button>
            
            {/* Statement B */}
            <button
              onClick={() => handleSelectResponse(currentPair.statement_b.code)}
              className={`w-full p-5 rounded-lg border-2 text-left transition-all duration-200 ${
                currentResponse === currentPair.statement_b.code
                  ? 'shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              style={currentResponse === currentPair.statement_b.code ? {
                borderColor: MEVIA_COLORS.primary,
                backgroundColor: MEVIA_COLORS.light
              } : {}}
            >
              <div className="flex items-start gap-4">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-medium ${
                    currentResponse === currentPair.statement_b.code
                      ? 'text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                  style={currentResponse === currentPair.statement_b.code ? {
                    backgroundColor: MEVIA_COLORS.primary
                  } : {}}
                >
                  B
                </div>
                <p className="text-gray-800 leading-relaxed">
                  {currentPair.statement_b.text}
                </p>
              </div>
            </button>
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPrevious}
            disabled={currentPairIndex === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {txt.previous}
          </Button>
          
          {/* Quick navigation dots */}
          <div className="flex-1 flex justify-center gap-1 px-4 overflow-hidden">
            {pairs.slice(Math.max(0, currentPairIndex - 5), currentPairIndex + 6).map((pair, idx) => {
              const actualIndex = Math.max(0, currentPairIndex - 5) + idx;
              const isAnswered = responses[pair.pair_number];
              const isCurrent = actualIndex === currentPairIndex;
              return (
                <button
                  key={pair.pair_number}
                  onClick={() => setCurrentPairIndex(actualIndex)}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{
                    width: isCurrent ? '16px' : '8px',
                    backgroundColor: isCurrent 
                      ? MEVIA_COLORS.primary 
                      : isAnswered 
                        ? MEVIA_COLORS.accent 
                        : '#D1D5DB'
                  }}
                />
              );
            })}
          </div>
          
          {currentPairIndex === pairs.length - 1 ? (
            <Button
              onClick={handleComplete}
              disabled={submitting || answeredCount < 72}
              className="text-white"
              style={{ backgroundColor: MEVIA_COLORS.primary }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {txt.saving}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {txt.complete}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={goToNext}
              disabled={currentPairIndex === pairs.length - 1}
              variant="outline"
            >
              {txt.next}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
        
        {/* Bottom progress indicator */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <Clock className="w-4 h-4 inline-block mr-1" />
          {txt.progress}: {answeredCount} / 72 ({Math.round(progressPercentage)}%)
        </div>
      </main>
    </div>
  );
};

export default EnneagramAssessment;
